import type { AppConfig, Target } from "@ai-guidance/shared";
import type { CapturedSnapshot } from "./snapshot";

/**
 * Rezolvarea țintei în 4 trepte:
 * 1. idx → harta snapshot-ului trimis la întrebarea curentă (referință directă
 *    de nod: fără querySelector, deci nimic de potrivit și nimic de greșit);
 * 2. elementId → selectorul din configul publicat (sursa de încredere);
 * 3. selectorul primit direct în acțiune;
 * 4. căutare după text vizibil (fallback pentru DOM-uri instabile).
 *
 * idx e prima treaptă de când promptul duce un snapshot al paginii vii: modelul
 * alege un număr din lista pe care i-am dat-o, nu mai scrie selectori. Treptele
 * 2-4 rămân pentru cererile fără snapshot (SDK vechi din cache-ul browserului)
 * și pentru un eventual rollback.
 *
 * Returnează null dacă nimic nu se găsește - apelantul decide fallback-ul UX.
 */
export function resolveTarget(target: Target, config: AppConfig, captured?: CapturedSnapshot | null): Element | null {
  // Pe calea vocală acțiunile vin fără structured output, deci o țintă poate
  // lipsi cu totul (validarea din voiceSplit o oprește la sursă, dar garda de
  // aici e ieftină și transformă o excepție mută în mesajul vizibil de
  // „target missing" - vizitatorul află, în loc să vadă un spotlight absent).
  if (!target) return null;
  if (target.idx !== undefined && captured) {
    const el = captured.map.get(target.idx);
    // Nodul poate să fi dispărut între captură și răspuns (re-randare, SPA):
    // `isConnected` e verificarea, iar textul din snapshot-ul NOSTRU e plasa de
    // siguranță - îl avem deja local, deci fallback-ul costă zero tokeni de
    // output (modelul n-a trebuit să ne trimită și o etichetă „pentru orice
    // eventualitate").
    if (el?.isConnected) return el;
    const capturedText = captured.snapshot.elements.find((e) => e.idx === target.idx)?.text;
    const found = capturedText ? findByText(capturedText) : null;
    if (found) return found;
  }

  if (target.elementId) {
    const scopes = [...(config.layout?.elements ? [config.layout] : []), ...config.pages];
    for (const scope of scopes) {
      for (const el of scope.elements) {
        if (el.id === target.elementId) {
          const found = safeQuery(el.selector);
          if (found) return found;
        }
      }
    }
  }

  if (target.selector) {
    const found = safeQuery(target.selector);
    if (found) return found;
  }

  if (target.fallbackText) {
    return findByText(target.fallbackText);
  }

  return null;
}

function safeQuery(selector: string): Element | null {
  try {
    return document.querySelector(selector);
  } catch {
    return null;
  }
}

function findByText(text: string): Element | null {
  const needle = text.trim().toLowerCase();
  if (!needle) return null;
  const candidates = document.querySelectorAll(
    "button, a, [role='button'], label, th, h1, h2, h3, [data-ai-id]",
  );
  for (const el of candidates) {
    if ((el.textContent ?? "").trim().toLowerCase().includes(needle)) return el;
  }
  return null;
}

/** Destinația unei acțiuni `navigate`, deja ancorată într-o pagină CUNOSCUTĂ. */
export interface NavigateDestination {
  pathname: string;
  hash: string;
  /** pathname + hash - forma de dat lui `location.assign`. */
  full: string;
}

/**
 * Ancorează calea cerută de model într-o pagină din configul publicat.
 *
 * Modelul nu poate fi crezut pe cuvânt aici: în prima probă reală a cerut
 * „/pipeline" pentru o pagină care trăiește la „/orbit-crm/#/pipeline" -
 * `location.assign` pe calea inventată a însemnat reload pe un 404 și o
 * conversație vocală moartă (8 aug 2026). Regula: navigăm DOAR spre o pagină
 * pe care scanarea a văzut-o; potrivirea e pe sufixul de segmente („pipeline"
 * ⊂ „orbit-crm / pipeline"), preferând potrivirea exactă. Nimic potrivit →
 * null, iar apelantul tratează ca țintă lipsă - nu se navighează orbește.
 *
 * `opts.commerce` deschide o A DOUA treaptă, doar pe magazine: acolo paginile
 * de produs și de categorie nu sunt în scanare (mii de produse), iar calea
 * concretă vine din catalog - validată server-side înainte să ajungă aici
 * (commerceActions.ts → navigateAllowed: pagină scanată, produs sau colecție
 * din catalog; orice altceva e aruncat ACOLO, cu `navigate_dropped`). Deci pe
 * commerce orice cale same-origin e o destinație literală. Până pe 22 aug 2026
 * treapta asta cerea și un pattern cu wildcard din config („/products/*", pus
 * de mână pe demo3): pe un magazin Woo fără „/produs/*" pagina de produs,
 * validată în catalog, era refuzată în browser cu „nu găsesc elementul"
 * (Nordicshops #539, #541). Fără commerce, nimic nu se navighează în afara
 * scanării: acolo backendul nu are catalog cu care să garanteze existența.
 */
export function resolveNavigatePath(
  raw: string,
  config: AppConfig,
  opts?: { commerce?: boolean },
): NavigateDestination | null {
  const want = pathSegments(raw);

  let best: { extra: number; dest: NavigateDestination } | null = null;
  for (const page of config.pages) {
    const pattern = page.urlPattern ?? "";
    // Șabloanele cu wildcard descriu o FAMILIE de pagini - nu există o
    // destinație concretă spre care să navigăm.
    if (!pattern || pattern.includes("*")) continue;
    let url: URL;
    try {
      url = new URL(pattern, location.origin);
    } catch {
      continue;
    }
    // Un pattern de pe alt host (config scanat pe alt mediu) nu e o destinație
    // validă aici - navigate promite same-origin.
    if (url.origin !== location.origin) continue;
    const have = pathSegments(url.pathname + url.hash);
    if (!segmentsEndWith(have, want)) continue;
    const extra = have.length - want.length;
    if (best === null || extra < best.extra) {
      best = {
        extra,
        dest: { pathname: url.pathname, hash: url.hash, full: url.pathname + url.hash },
      };
      // Potrivire exactă - nu există ceva mai bun de căutat.
      if (extra === 0) break;
    }
  }
  if (best) return best.dest;
  return opts?.commerce ? literalSameOrigin(raw) : null;
}

/**
 * A doua treaptă, doar pe commerce: calea cerută, LITERAL - cu o singură
 * condiție, same-origin. Garanția că pagina EXISTĂ o dă backendul, din
 * catalog (navigateAllowed); aici verificăm doar că vizitatorul rămâne pe
 * situl lui - navigate promite same-origin, mereu.
 */
function literalSameOrigin(raw: string): NavigateDestination | null {
  let target: URL;
  try {
    target = new URL(raw, location.origin);
  } catch {
    return null;
  }
  if (target.origin !== location.origin) return null;
  return { pathname: target.pathname, hash: target.hash, full: target.pathname + target.hash };
}

/** „/orbit-crm/#/pipeline" → ["orbit-crm", "pipeline"]: identitate fără separatori. */
function pathSegments(path: string): string[] {
  return path
    .split(/[/#?]+/)
    .map((s) => s.trim().toLowerCase())
    .filter((s) => s.length > 0);
}

function segmentsEndWith(have: string[], want: string[]): boolean {
  // Rădăcina e cazul degenerat: `want` gol se potrivește cu ORICE `have` prin
  // `every` (adevăr vacuu), nu doar cu o pagină care e ea însăși rădăcina -
  // aceeași capcană ca în commerceActions.ts (backend), reparată identic.
  if (want.length === 0) return have.length === 0;
  if (want.length > have.length) return false;
  return want.every((seg, i) => have[have.length - want.length + i] === seg);
}
